"use client";

import { useAuth } from "@/components/AuthContext";
import { ResponsiveLanguagePicker } from "@/components/language/ResponsiveLanguagePicker";
import Link from "@/components/school/SchoolLink";
import { useLanguageJourneyCatalog } from "@/hooks/useLanguageJourneyCatalog";
import { useRouter } from "@/hooks/useSchoolRouter";
import {
  cinematicModalInputClass,
  cinematicModalPrimaryButtonClass,
  cinematicModalSecondaryButtonClass,
  cinematicModalSectionClass,
  cinematicModalSelectClass,
  cinematicModalTextareaClass,
} from "@/lib/cinematicModalStyles";
import { preloadLanguageJourney } from "@/lib/languageJourneyClient";
import {
  type LanguageJourney,
  type LanguageJourneyChapter,
  getLanguageJourneyByCode,
  getLanguageJourneyChapterDisplayDescription,
  getLanguageJourneyChapterDisplayFocus,
  getLanguageJourneyChapterGroups,
} from "@/lib/languageJourneyClient";
import { getPodcastDiscussionHref, podcastHasDiscussableScript } from "@/lib/podcasts/discussion";
import { PODCAST_SUPPORTED_LANGUAGES } from "@/lib/podcasts/languages";
import { getPodcastSpeakerLabel } from "@/lib/podcasts/personas";
import { supabase } from "@/lib/supabaseClient";
import { cn } from "@/lib/utils";
import {
  type ManualPodcastSegment,
  PODCAST_VOICE_BY_SPEAKER,
  type PodcastCefrLevel,
  type PodcastDialogueLength,
  type PodcastProject,
  type PodcastSpeakerRole,
  type PodcastTranscriptSegment,
} from "@/models/podcast";
import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  Download,
  Edit3,
  Globe2,
  Headphones,
  Library,
  Loader2,
  Lock,
  MessageCircle,
  Mic2,
  Play,
  Plus,
  Save,
  Sparkles,
  StopCircle,
  Upload,
  Volume2,
} from "lucide-react";
import { useSearchParams } from "next/navigation";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const CEFR_LEVELS: PodcastCefrLevel[] = ["A1", "A2", "B1", "B2", "C1", "C2"];
const STORAGE_NOTICE =
  "Sign in to save podcasts, chapter listening tracks, and generated audio permanently.";
type PodcastSearchParams = Pick<URLSearchParams, "get" | "toString">;

function normalizeCefrLevel(value: string | null | undefined): PodcastCefrLevel {
  const normalized = value?.trim().toUpperCase();
  if (normalized && CEFR_LEVELS.includes(normalized as PodcastCefrLevel)) {
    return normalized as PodcastCefrLevel;
  }

  switch (normalized) {
    case "ABSOLUTE_BEGINNER":
      return "A1";
    case "BEGINNER":
      return "A2";
    case "INTERMEDIATE":
      return "B1";
    case "ADVANCED":
      return "B2";
    default:
      return "B1";
  }
}

function getPodcastLanguageNameFromParams(searchParams: PodcastSearchParams) {
  const languageName =
    searchParams.get("targetLanguage") ||
    searchParams.get("languageName") ||
    searchParams.get("language") ||
    searchParams.get("target");

  if (languageName && PODCAST_SUPPORTED_LANGUAGES[languageName]) {
    return languageName;
  }

  const languageCode =
    searchParams.get("targetLanguageCode") ||
    searchParams.get("languageCode") ||
    searchParams.get("targetCode");

  if (languageCode) {
    const exactMatch = Object.entries(PODCAST_SUPPORTED_LANGUAGES).find(
      ([, code]) => code === languageCode
    );
    if (exactMatch) return exactMatch[0];

    const baseCode = languageCode.split("-")[0];
    const baseMatch = Object.entries(PODCAST_SUPPORTED_LANGUAGES).find(
      ([, code]) => code.split("-")[0] === baseCode
    );
    if (baseMatch) return baseMatch[0];
  }

  return "Dutch";
}

function createEmptyPodcast(searchParams?: PodcastSearchParams): PodcastProject {
  const now = Date.now();
  const targetLanguage = searchParams ? getPodcastLanguageNameFromParams(searchParams) : "Dutch";
  const cefrLevel = normalizeCefrLevel(
    searchParams?.get("cefrLevel") ||
      searchParams?.get("level") ||
      searchParams?.get("languageLevel")
  );

  return {
    id: crypto.randomUUID(),
    title: "",
    topic: "",
    targetLanguage,
    targetLanguageCode: PODCAST_SUPPORTED_LANGUAGES[targetLanguage],
    cefrLevel,
    source: "ai_generated",
    status: "draft",
    visibility: "private",
    segments: [],
    createdAt: now,
    updatedAt: now,
  };
}

async function authHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const headers: Record<string, string> = {};
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }
  return headers;
}

function base64ToBlob(base64: string, contentType: string) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: contentType });
}

async function downloadPodcastWav(project: PodcastProject) {
  const audioSegments = project.segments.filter((segment) => segment.audioUrl);
  if (audioSegments.length === 0) return;

  const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
  const audioContext = new AudioContextClass({ sampleRate: 24000 });
  const pcmChunks: Float32Array[] = [];

  for (const segment of audioSegments) {
    if (!segment.audioUrl) continue;
    const response = await fetch(segment.audioUrl);
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    pcmChunks.push(audioBuffer.getChannelData(0));
  }

  await audioContext.close();

  const totalLength = pcmChunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const samples = new Int16Array(totalLength);
  let offset = 0;
  for (const chunk of pcmChunks) {
    for (let index = 0; index < chunk.length; index += 1) {
      const sample = Math.max(-1, Math.min(1, chunk[index]));
      samples[offset] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      offset += 1;
    }
  }

  const wavBlob = createWavFromInt16(samples, 24000);
  const url = URL.createObjectURL(wavBlob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${project.title || "chickytutor-podcast"}.wav`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function createWavFromInt16(samples: Int16Array, sampleRate: number) {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeString = (offset: number, value: string) => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(offset + index, value.charCodeAt(index));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, samples.length * 2, true);
  new Int16Array(buffer, 44).set(samples);
  return new Blob([buffer], { type: "audio/wav" });
}

function segmentTextForSpeech(segment: PodcastTranscriptSegment) {
  return segment.ssmlText || segment.text;
}

async function readPodcastApiResponse(response: Response) {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }

  const bodyText = await response.text();
  const message = response.ok
    ? "Podcast API returned a non-JSON response."
    : `Podcast API returned ${response.status} ${response.statusText || "error"}.`;

  return {
    error: bodyText.includes("<!DOCTYPE")
      ? `${message} The dev server returned an HTML page instead of JSON. Restart the Next dev server so API routes are reloaded.`
      : `${message} ${bodyText.slice(0, 200)}`.trim(),
  };
}

function getPodcastChapterLanguageCode(item: PodcastProject) {
  return (
    item.chapterLanguageCode ||
    item.targetLanguageCode ||
    PODCAST_SUPPORTED_LANGUAGES[item.targetLanguage] ||
    null
  );
}

function BetaBadge({ label = "Beta" }: { label?: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-orange-300/35 bg-orange-400/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-normal text-orange-200">
      {label}
    </span>
  );
}

export function PodcastWorkspace() {
  const { user, isLoading: authLoading, openAuthModal } = useAuth();
  const searchParams = useSearchParams();
  const router = useRouter();

  const [activeView, setActiveView] = useState<"listening-room" | "create" | "manual" | "editor">(
    "listening-room"
  );
  const [feedScope, setFeedScope] = useState<"public" | "mine">("public");
  const [selectedLanguage, setSelectedLanguage] = useState("All");
  const [feedItems, setFeedItems] = useState<PodcastProject[]>([]);
  const [isFeedLoading, setIsFeedLoading] = useState(false);
  const [isFeedLoadingMore, setIsFeedLoadingMore] = useState(false);
  const [nextFeedOffset, setNextFeedOffset] = useState<number | null>(null);
  const [project, setProject] = useState<PodcastProject>(() => createEmptyPodcast(searchParams));
  const [manualInput, setManualInput] = useState("");
  const [manualSegments, setManualSegments] = useState<ManualPodcastSegment[]>([]);
  const [includeExplanation, setIncludeExplanation] = useState(true);
  const [dialogueLength, setDialogueLength] = useState<PodcastDialogueLength>("short");
  const [isWorking, setIsWorking] = useState(false);
  const [workingLabel, setWorkingLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [editingSegmentId, setEditingSegmentId] = useState<string | null>(null);
  const [draftText, setDraftText] = useState("");
  const [newSegmentSpeaker, setNewSegmentSpeaker] = useState<PodcastSpeakerRole>("Teacher");
  const [newSegmentText, setNewSegmentText] = useState("");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const chapterBootstrappedRef = useRef(false);

  const languageOptions = useMemo(
    () => ["All", ...Object.keys(PODCAST_SUPPORTED_LANGUAGES).sort((a, b) => a.localeCompare(b))],
    []
  );

  const projectCanSave = Boolean(user);
  const audioReadyCount = project.segments.filter((segment) => segment.audioUrl).length;
  const allAudioReady = project.segments.length > 0 && audioReadyCount === project.segments.length;
  const hasTrackOpen = project.segments.length > 0 || activeView === "editor";

  const createPrefilledPodcast = useCallback(
    () => createEmptyPodcast(new URLSearchParams(searchParams.toString())),
    [searchParams]
  );

  useEffect(() => {
    const prefilled = createPrefilledPodcast();
    setProject((current) => {
      if (
        current.status !== "draft" ||
        current.segments.length > 0 ||
        current.title ||
        current.topic
      ) {
        return current;
      }
      if (
        current.targetLanguage === prefilled.targetLanguage &&
        current.cefrLevel === prefilled.cefrLevel
      ) {
        return current;
      }
      return {
        ...current,
        targetLanguage: prefilled.targetLanguage,
        targetLanguageCode: prefilled.targetLanguageCode,
        cefrLevel: prefilled.cefrLevel,
      };
    });
  }, [createPrefilledPodcast]);

  const saveProject = useCallback(
    async (nextProject: PodcastProject) => {
      if (!user) {
        setProject(nextProject);
        return nextProject;
      }

      const headers = await authHeaders();
      const response = await fetch("/api/podcasts", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(nextProject),
      });
      const data = await readPodcastApiResponse(response);
      if (!response.ok) throw new Error(data.error || "Failed to save podcast");
      setProject(data.podcast);
      return data.podcast as PodcastProject;
    },
    [user]
  );

  const fetchFeedPage = useCallback(
    async (offset: number) => {
      const headers = await authHeaders();
      const params = new URLSearchParams({
        scope: feedScope,
        language: selectedLanguage,
        limit: feedScope === "mine" ? "50" : "500",
        offset: String(offset),
      });
      const response = await fetch(`/api/podcasts?${params}`, { headers });
      const data = await readPodcastApiResponse(response);
      if (!response.ok) throw new Error(data.error || "Failed to load podcasts");
      return data;
    },
    [feedScope, selectedLanguage]
  );

  const refreshFeed = useCallback(async () => {
    setIsFeedLoading(true);
    setNextFeedOffset(null);
    try {
      const data = await fetchFeedPage(0);
      setFeedItems(data.podcasts || []);
      setNextFeedOffset(data.pagination?.nextOffset ?? null);
    } catch {
      setError("We couldn't load your podcasts. Check your connection and try again.");
    } finally {
      setIsFeedLoading(false);
    }
  }, [fetchFeedPage]);

  const loadMoreFeed = useCallback(async () => {
    if (nextFeedOffset === null || isFeedLoadingMore) return;
    setIsFeedLoadingMore(true);
    try {
      const data = await fetchFeedPage(nextFeedOffset);
      setFeedItems((current) => {
        const itemsById = new Map(current.map((item) => [item.id, item]));
        for (const item of data.podcasts || []) itemsById.set(item.id, item);
        return [...itemsById.values()];
      });
      setNextFeedOffset(data.pagination?.nextOffset ?? null);
    } catch {
      setError("We couldn't load more podcasts. Check your connection and try again.");
    } finally {
      setIsFeedLoadingMore(false);
    }
  }, [fetchFeedPage, isFeedLoadingMore, nextFeedOffset]);

  useEffect(() => {
    refreshFeed();
  }, [refreshFeed]);

  useEffect(() => {
    const podcastId = searchParams.get("podcastId");
    const languageCode = searchParams.get("languageCode");
    const chapterId = searchParams.get("chapterId");

    if (chapterBootstrappedRef.current) return;
    if (authLoading) return;
    if (!podcastId && (!languageCode || !chapterId)) return;
    chapterBootstrappedRef.current = true;

    const load = async () => {
      setIsWorking(true);
      setError(null);
      try {
        const headers = await authHeaders();

        if (podcastId) {
          const response = await fetch(`/api/podcasts/${podcastId}`, { headers });
          const data = await readPodcastApiResponse(response);
          if (!response.ok) throw new Error(data.error || "Podcast not found");
          setProject(data.podcast);
          setActiveView("editor");
          return;
        }

        setWorkingLabel("Preparing this chapter podcast");
        const response = await fetch("/api/podcasts/chapter", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...headers },
          body: JSON.stringify({ languageCode, chapterId }),
        });
        const data = await readPodcastApiResponse(response);
        if (response.status === 401 && !user) {
          openAuthModal("signup");
          throw new Error(STORAGE_NOTICE);
        }
        if (!response.ok) throw new Error(data.error || "Failed to load chapter podcast");
        setProject(data.podcast);
        setActiveView("editor");
        if (data.generated || data.podcast.status !== "audio_ready") {
          await generateMissingAudio(data.podcast, true);
        }
      } catch {
        setError("We couldn't open this podcast. Please try again.");
      } finally {
        setIsWorking(false);
        setWorkingLabel("");
      }
    };

    load();
  }, [authLoading, openAuthModal, searchParams, user]);

  const handleCreateScript = async () => {
    if (!user) {
      openAuthModal("signup");
      setError(STORAGE_NOTICE);
      return;
    }

    if (!project.title.trim() || !project.topic.trim()) {
      setError("Add a title and topic first.");
      return;
    }

    setIsWorking(true);
    setWorkingLabel("Generating script");
    setError(null);
    try {
      const headers = await authHeaders();
      const response = await fetch("/api/podcasts/generate-script", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({
          topic: project.topic,
          targetLanguage: project.targetLanguage,
          cefrLevel: project.cefrLevel,
          includeExplanation,
          dialogueLength,
          explanationLanguage: "English",
        }),
      });
      const data = await readPodcastApiResponse(response);
      if (!response.ok) throw new Error(data.error || "Failed to generate script");

      const nextProject: PodcastProject = {
        ...project,
        targetLanguageCode: data.targetLanguageCode,
        status: "scripted",
        source: "ai_generated",
        segments: data.segments,
        updatedAt: Date.now(),
      };
      await saveProject(nextProject);
      setActiveView("editor");
      refreshFeed();
    } catch {
      setError("We couldn't create the podcast script. Please try again.");
    } finally {
      setIsWorking(false);
      setWorkingLabel("");
    }
  };

  const handleManualProcess = async () => {
    if (!user) {
      openAuthModal("signup");
      setError(STORAGE_NOTICE);
      return;
    }

    if (!manualInput.trim()) {
      setError("Paste a script first.");
      return;
    }

    setIsWorking(true);
    setWorkingLabel("Formatting manual script");
    setError(null);
    try {
      const headers = await authHeaders();
      const response = await fetch("/api/podcasts/manual-process", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({
          text: manualInput,
          targetLanguage: project.targetLanguage,
        }),
      });
      const data = await readPodcastApiResponse(response);
      if (!response.ok) throw new Error(data.error || "Failed to process script");
      setManualSegments(data.manualSegments || []);

      const nextProject: PodcastProject = {
        ...project,
        title: project.title || `${project.targetLanguage} Listening Track`,
        topic: project.topic || "Manual script",
        targetLanguageCode: data.targetLanguageCode,
        source: "manual",
        status: "scripted",
        segments: data.segments,
        updatedAt: Date.now(),
      };
      await saveProject(nextProject);
      setActiveView("editor");
      refreshFeed();
    } catch {
      setError("We couldn't prepare this script. Check the text and try again.");
    } finally {
      setIsWorking(false);
      setWorkingLabel("");
    }
  };

  async function generateMissingAudio(baseProject = project, quiet = false) {
    if (!user) {
      openAuthModal("signup");
      throw new Error(STORAGE_NOTICE);
    }

    let nextProject = baseProject;
    if (!quiet) {
      setIsWorking(true);
      setWorkingLabel("Generating audio");
    }
    setError(null);

    try {
      for (let index = 0; index < nextProject.segments.length; index += 1) {
        const segment = nextProject.segments[index];
        if (segment.audioUrl) continue;

        if (!quiet) setWorkingLabel(`Generating audio ${index + 1}/${nextProject.segments.length}`);
        const headers = await authHeaders();
        const generated = await fetch("/api/podcasts/generate-segment-audio", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...headers },
          body: JSON.stringify({
            text: segmentTextForSpeech(segment),
            speaker: segment.speaker,
            voiceName: segment.voiceName || PODCAST_VOICE_BY_SPEAKER[segment.speaker],
            languageCode: segment.language || nextProject.targetLanguageCode || "en-US",
          }),
        });
        const generatedData = await readPodcastApiResponse(generated);
        if (!generated.ok) throw new Error(generatedData.error || "Failed to generate audio");

        const uploaded = await fetch(`/api/podcasts/${nextProject.id}/audio`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...headers },
          body: JSON.stringify({
            segmentId: segment.id,
            audioData: generatedData.audioData,
            contentType: generatedData.contentType,
            extension: generatedData.format || "mp3",
          }),
        });
        const uploadData = await readPodcastApiResponse(uploaded);
        if (!uploaded.ok) throw new Error(uploadData.error || "Failed to upload audio");

        const blob = base64ToBlob(generatedData.audioData, generatedData.contentType);
        const duration = await estimateAudioDuration(blob);
        nextProject = {
          ...nextProject,
          segments: nextProject.segments.map((candidate) =>
            candidate.id === segment.id
              ? { ...candidate, audioUrl: uploadData.audioUrl, audioDuration: duration }
              : candidate
          ),
        };
        setProject(nextProject);
      }

      nextProject = {
        ...nextProject,
        status: "audio_ready",
        updatedAt: Date.now(),
      };
      await saveProject(nextProject);
      refreshFeed();
      return nextProject;
    } finally {
      if (!quiet) {
        setIsWorking(false);
        setWorkingLabel("");
      }
    }
  }

  async function estimateAudioDuration(blob: Blob) {
    return new Promise<number>((resolve) => {
      const audio = document.createElement("audio");
      const url = URL.createObjectURL(blob);
      audio.preload = "metadata";
      audio.onloadedmetadata = () => {
        const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
        URL.revokeObjectURL(url);
        resolve(duration);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(0);
      };
      audio.src = url;
    });
  }

  const playFrom = async (index: number) => {
    const segment = project.segments[index];
    if (!segment?.audioUrl) return;
    audioRef.current?.pause();
    const audio = new Audio(segment.audioUrl);
    audioRef.current = audio;
    setPlayingIndex(index);
    setIsPlaying(true);
    audio.onended = () => {
      const nextIndex = project.segments.findIndex(
        (candidate, candidateIndex) => candidateIndex > index && candidate.audioUrl
      );
      if (nextIndex >= 0) {
        playFrom(nextIndex);
      } else {
        setIsPlaying(false);
        setPlayingIndex(null);
      }
    };
    await audio.play();
  };

  const stopPlayback = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setIsPlaying(false);
    setPlayingIndex(null);
  };

  const updateSegment = async (
    segmentId: string,
    patch: Partial<PodcastTranscriptSegment>,
    clearAudio = true
  ) => {
    const nextProject = {
      ...project,
      status: "scripted" as const,
      segments: project.segments.map((segment) =>
        segment.id === segmentId
          ? {
              ...segment,
              ...patch,
              ...(clearAudio ? { audioUrl: undefined, audioDuration: undefined } : {}),
            }
          : segment
      ),
      updatedAt: Date.now(),
    };
    await saveProject(nextProject);
  };

  const regenerateSegmentAudio = async (segmentId: string) => {
    const nextProject = {
      ...project,
      status: "scripted" as const,
      segments: project.segments.map((segment) =>
        segment.id === segmentId
          ? { ...segment, audioUrl: undefined, audioDuration: undefined }
          : segment
      ),
      updatedAt: Date.now(),
    };
    setProject(nextProject);
    await saveProject(nextProject);
    await generateMissingAudio(nextProject);
  };

  const addSegment = async () => {
    if (!newSegmentText.trim()) return;
    const speaker = newSegmentSpeaker;
    const segment: PodcastTranscriptSegment = {
      id: `seg-${Date.now()}`,
      speaker,
      text: newSegmentText.trim(),
      isEdited: true,
      voiceName: PODCAST_VOICE_BY_SPEAKER[speaker],
    };
    const nextProject = {
      ...project,
      status: "scripted" as const,
      segments: [...project.segments, segment],
      updatedAt: Date.now(),
    };
    setNewSegmentText("");
    await saveProject(nextProject);
  };

  return (
    <main className="min-h-screen bg-[#0f0f14] text-white">
      <header className="border-b border-white/10 bg-[#121318]">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 sm:py-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-[10px] font-black uppercase text-orange-300/75"
            >
              <Headphones className="h-3.5 w-3.5" aria-hidden="true" />
              Listening workspace
            </Link>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <h2 className="text-2xl font-black tracking-tight sm:text-3xl">Listening Podcasts</h2>
              <BetaBadge />
            </div>
            <p className="mt-1 max-w-2xl text-xs leading-relaxed text-white/45 sm:text-sm">
              Podcast features are in beta while creation, audio, and chapter coverage are still
              being finished.
            </p>
            <Link
              href="#public-listening-courses"
              className="mt-2 inline-flex text-xs font-bold text-orange-300 transition-colors hover:text-orange-200"
            >
              Browse the public course directory ↓
            </Link>
          </div>
          <nav className="flex flex-wrap gap-2" aria-label="Podcast workspace">
            <WorkspaceTab
              icon={<Globe2 size={16} />}
              label="Listening Room"
              active={activeView === "listening-room"}
              onClick={() => {
                stopPlayback();
                setActiveView("listening-room");
                router.replace("/podcasts");
              }}
            />
            {hasTrackOpen && (
              <WorkspaceTab
                icon={<Headphones size={16} />}
                label="Current Track"
                active={activeView === "editor"}
                onClick={() => setActiveView("editor")}
              />
            )}
            <WorkspaceTab
              icon={<Sparkles size={16} />}
              label="Generate"
              active={activeView === "create"}
              onClick={() => {
                setProject(createPrefilledPodcast());
                setActiveView("create");
              }}
            />
            <WorkspaceTab
              icon={<Edit3 size={16} />}
              label="Script Editor"
              active={activeView === "manual"}
              onClick={() => {
                setProject(createPrefilledPodcast());
                setActiveView("manual");
              }}
            />
          </nav>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-5 px-4 py-5 sm:px-6 lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-6">
        <aside className="order-2 space-y-4 lg:order-1">
          <section className="rounded-lg border border-white/10 bg-[#121318] p-4">
            <div className="flex items-center gap-2 text-[10px] font-black uppercase text-orange-300/70">
              <Library size={16} />
              Library
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                onClick={() => setFeedScope("public")}
                className={cn(
                  "min-h-10 rounded-lg border px-3 py-2 text-sm font-bold transition-colors",
                  feedScope === "public"
                    ? "border-orange-300/55 bg-orange-500/10 text-white"
                    : "border-white/10 bg-white/[0.025] text-white/55 hover:bg-white/[0.055] hover:text-white"
                )}
              >
                Public
              </button>
              <button
                onClick={() => setFeedScope("mine")}
                className={cn(
                  "min-h-10 rounded-lg border px-3 py-2 text-sm font-bold transition-colors",
                  feedScope === "mine"
                    ? "border-orange-300/55 bg-orange-500/10 text-white"
                    : "border-white/10 bg-white/[0.025] text-white/55 hover:bg-white/[0.055] hover:text-white"
                )}
              >
                Mine
              </button>
            </div>
            <label className="mt-4 block text-[10px] font-black uppercase text-white/40">
              Language
            </label>
            <PodcastLanguageDropdown
              languages={languageOptions}
              selectedLanguage={selectedLanguage}
              onSelect={setSelectedLanguage}
            />
            {!user && !authLoading && (
              <button
                onClick={() => openAuthModal("signup")}
                className={cn(cinematicModalPrimaryButtonClass, "mt-4 w-full")}
              >
                <Lock size={15} />
                Save podcasts
              </button>
            )}
          </section>

          <section className="rounded-lg border border-white/10 bg-[#121318] p-4">
            <div className="flex items-center gap-2 text-[10px] font-black uppercase text-orange-300/70">
              <Headphones size={16} />
              Current
            </div>
            <button
              onClick={() => project.segments.length > 0 && setActiveView("editor")}
              disabled={project.segments.length === 0}
              className="mt-3 block w-full rounded-lg border border-white/10 bg-white/[0.025] px-3 py-2.5 text-left text-sm font-bold text-white/75 transition-colors hover:bg-white/[0.05] disabled:cursor-default disabled:opacity-55"
            >
              {project.title || "No podcast selected"}
            </button>
            {project.segments.length > 0 && (
              <div className="mt-2 text-[11px] font-semibold text-white/40">
                {project.targetLanguage} / {project.cefrLevel} / {audioReadyCount}/
                {project.segments.length} audio segments
              </div>
            )}
          </section>
        </aside>

        <section className="order-1 min-w-0 lg:order-2">
          {error && (
            <div className="mb-4 rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
              {error}
            </div>
          )}

          {isWorking && (
            <div className="mb-4 flex items-center gap-3 rounded-lg border border-orange-300/25 bg-orange-500/10 px-4 py-3 text-sm text-orange-100">
              <Loader2 size={18} className="animate-spin" />
              {workingLabel || "Working"}
            </div>
          )}

          {activeView === "listening-room" && (
            <ReadingRoom
              feedItems={feedItems}
              isFeedLoading={isFeedLoading}
              isFeedLoadingMore={isFeedLoadingMore}
              hasMore={nextFeedOffset !== null}
              selectedLanguage={selectedLanguage}
              onLoadMore={loadMoreFeed}
              onOpen={(item) => {
                setProject(item);
                setActiveView("editor");
              }}
            />
          )}

          {activeView === "create" && (
            <ProjectForm
              project={project}
              setProject={setProject}
              includeExplanation={includeExplanation}
              setIncludeExplanation={setIncludeExplanation}
              dialogueLength={dialogueLength}
              setDialogueLength={setDialogueLength}
              onSubmit={handleCreateScript}
              submitLabel="Generate podcast script"
              submitIcon={<Sparkles size={17} />}
              disabled={isWorking}
            />
          )}

          {activeView === "manual" && (
            <div className="space-y-4">
              <ProjectForm
                project={project}
                setProject={setProject}
                onSubmit={handleManualProcess}
                submitLabel="Convert script"
                submitIcon={<Upload size={17} />}
                disabled={isWorking}
                compact
              />
              <textarea
                value={manualInput}
                onChange={(event) => setManualInput(event.target.value)}
                placeholder="Speaker 1: We can talk about today's meeting.
Speaker 2: Good, I need useful phrases."
                className={cn(cinematicModalTextareaClass, "min-h-[260px]")}
              />
              {manualSegments.length > 0 && (
                <p className="text-sm text-white/55">
                  {manualSegments.length} manual segments converted. Open the editor to review or
                  generate audio.
                </p>
              )}
            </div>
          )}

          {activeView === "editor" && (
            <EditorView
              project={project}
              projectCanSave={projectCanSave}
              allAudioReady={allAudioReady}
              audioReadyCount={audioReadyCount}
              isPlaying={isPlaying}
              playingIndex={playingIndex}
              editingSegmentId={editingSegmentId}
              draftText={draftText}
              newSegmentSpeaker={newSegmentSpeaker}
              newSegmentText={newSegmentText}
              onSetProject={setProject}
              onSave={() => saveProject(project)}
              onGenerateAudio={() => generateMissingAudio()}
              onPlay={() => playFrom(0)}
              onStop={stopPlayback}
              onDownload={() => downloadPodcastWav(project)}
              onPlaySegment={playFrom}
              onEditStart={(segment) => {
                setEditingSegmentId(segment.id);
                setDraftText(segment.text);
              }}
              onEditCancel={() => {
                setEditingSegmentId(null);
                setDraftText("");
              }}
              onDraftTextChange={setDraftText}
              onEditSave={async (segment) => {
                await updateSegment(segment.id, {
                  text: draftText,
                  originalText: segment.originalText || segment.text,
                  ssmlText: undefined,
                  isEdited: true,
                });
                setEditingSegmentId(null);
                setDraftText("");
              }}
              onRegenerateSegmentAudio={regenerateSegmentAudio}
              onNewSegmentSpeakerChange={setNewSegmentSpeaker}
              onNewSegmentTextChange={setNewSegmentText}
              onAddSegment={addSegment}
              onBack={() => {
                stopPlayback();
                setActiveView("listening-room");
                router.replace("/podcasts");
              }}
            />
          )}
        </section>
      </div>
    </main>
  );
}

function WorkspaceTab({
  icon,
  label,
  active,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex min-h-10 items-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60",
        active
          ? "border-orange-300/65 bg-orange-500 text-white"
          : "border-white/10 bg-white/[0.025] text-white/55 hover:border-orange-300/30 hover:bg-white/[0.055] hover:text-white"
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function PodcastLanguageDropdown({
  languages,
  selectedLanguage,
  onSelect,
}: {
  languages: string[];
  selectedLanguage: string;
  onSelect: (language: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const languageOptions = useMemo(
    () => Object.fromEntries(languages.map((language) => [language, language])),
    [languages]
  );

  return (
    <ResponsiveLanguagePicker
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button
          type="button"
          className="mt-2 flex h-12 w-full items-center gap-3 rounded-lg border border-white/10 bg-white/[0.04] px-3 text-left text-white transition-colors hover:border-orange-300/35 hover:bg-white/[0.06] focus:border-orange-300/55 focus:outline-none focus:ring-2 focus:ring-orange-300/25"
          aria-label="Select podcast language"
        >
          <Globe2 size={18} className="shrink-0 text-orange-300" />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold">{selectedLanguage}</span>
          <ChevronDown size={18} className="shrink-0 text-white/70" />
        </button>
      }
      languages={languageOptions}
      selectedLanguageCode={selectedLanguage}
      onSelectLanguage={onSelect}
      searchTerm={searchTerm}
      onSearchTermChange={setSearchTerm}
      popularValues={[]}
      title="Choose a podcast language"
      desktopAlign="start"
    />
  );
}

function ProjectForm({
  project,
  setProject,
  includeExplanation,
  setIncludeExplanation,
  dialogueLength,
  setDialogueLength,
  onSubmit,
  submitLabel,
  submitIcon,
  disabled,
  compact = false,
}: {
  project: PodcastProject;
  setProject: (project: PodcastProject) => void;
  includeExplanation?: boolean;
  setIncludeExplanation?: (include: boolean) => void;
  dialogueLength?: PodcastDialogueLength;
  setDialogueLength?: (length: PodcastDialogueLength) => void;
  onSubmit: () => void;
  submitLabel: string;
  submitIcon: ReactNode;
  disabled: boolean;
  compact?: boolean;
}) {
  return (
    <div className={cinematicModalSectionClass}>
      {!compact && (
        <div className="mb-4">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-black">Create a listening podcast</h2>
            <BetaBadge />
          </div>
          <p className="mt-1 text-sm text-white/55">
            Generate a CEFR-matched dialogue with optional teacher breakdowns. Review beta output
            before sharing.
          </p>
        </div>
      )}
      {!compact && setIncludeExplanation && setDialogueLength && (
        <div className="mb-4 grid gap-3 md:grid-cols-2">
          <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.025] px-3 py-2.5 text-sm text-white/75">
            <input
              type="checkbox"
              checked={includeExplanation}
              onChange={(event) => setIncludeExplanation(event.target.checked)}
            />
            Include vocabulary and grammar breakdowns
          </label>
          <label className="block">
            <span className="text-[10px] font-black uppercase text-white/40">Length</span>
            <select
              value={dialogueLength || "short"}
              onChange={(event) => setDialogueLength(event.target.value as PodcastDialogueLength)}
              className={cn(cinematicModalSelectClass, "mt-2")}
            >
              <option value="short">Short session</option>
              <option value="long">Longer session</option>
            </select>
          </label>
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        <label className="block">
          <span className="text-[10px] font-black uppercase text-white/40">Title</span>
          <input
            value={project.title}
            onChange={(event) => setProject({ ...project, title: event.target.value })}
            className={cn(cinematicModalInputClass, "mt-2")}
            placeholder="A real lunch break conversation"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-black uppercase text-white/40">Topic</span>
          <input
            value={project.topic}
            onChange={(event) => setProject({ ...project, topic: event.target.value })}
            className={cn(cinematicModalInputClass, "mt-2")}
            placeholder="Negotiating plans after work"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-black uppercase text-white/40">Target language</span>
          <select
            value={project.targetLanguage}
            onChange={(event) =>
              setProject({
                ...project,
                targetLanguage: event.target.value,
                targetLanguageCode: PODCAST_SUPPORTED_LANGUAGES[event.target.value],
              })
            }
            className={cn(cinematicModalSelectClass, "mt-2")}
          >
            {Object.keys(PODCAST_SUPPORTED_LANGUAGES)
              .sort((a, b) => a.localeCompare(b))
              .map((language) => (
                <option key={language} value={language}>
                  {language}
                </option>
              ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] font-black uppercase text-white/40">CEFR</span>
            <select
              value={project.cefrLevel}
              onChange={(event) =>
                setProject({ ...project, cefrLevel: event.target.value as PodcastCefrLevel })
              }
              className={cn(cinematicModalSelectClass, "mt-2")}
            >
              {CEFR_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-black uppercase text-white/40">Visibility</span>
            <select
              value={project.visibility}
              onChange={(event) =>
                setProject({
                  ...project,
                  visibility: event.target.value as PodcastProject["visibility"],
                })
              }
              className={cn(cinematicModalSelectClass, "mt-2")}
            >
              <option value="private">Private</option>
              <option value="public">Public</option>
            </select>
          </label>
        </div>
      </div>
      <button
        onClick={onSubmit}
        disabled={disabled}
        className={cn(cinematicModalPrimaryButtonClass, "mt-4")}
      >
        {submitIcon}
        {submitLabel}
      </button>
    </div>
  );
}

function ReadingRoom({
  feedItems,
  isFeedLoading,
  isFeedLoadingMore,
  hasMore,
  selectedLanguage,
  onLoadMore,
  onOpen,
}: {
  feedItems: PodcastProject[];
  isFeedLoading: boolean;
  isFeedLoadingMore: boolean;
  hasMore: boolean;
  selectedLanguage: string;
  onLoadMore: () => void;
  onOpen: (project: PodcastProject) => void;
}) {
  const selectedLanguageCode =
    selectedLanguage !== "All" ? PODCAST_SUPPORTED_LANGUAGES[selectedLanguage] : null;
  const [selectedChapterLanguageCode, setSelectedChapterLanguageCode] = useState<string | null>(
    selectedLanguageCode
  );
  const chapterItems = useMemo(
    () => feedItems.filter((item) => item.source === "chapter" && item.chapterId),
    [feedItems]
  );
  const { revision: journeyCatalogRevision } = useLanguageJourneyCatalog();
  const [catalogLoadStatuses, setCatalogLoadStatuses] = useState<
    Record<string, "loading" | "error" | "ready">
  >({});
  const [catalogLoadAttempt, setCatalogLoadAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const codes = new Set(chapterItems.map(getPodcastChapterLanguageCode));
    for (const code of codes) {
      if (!code || getLanguageJourneyByCode(code)) continue;
      setCatalogLoadStatuses((current) => ({ ...current, [code]: "loading" }));
      void preloadLanguageJourney(code).then(
        () => {
          if (!cancelled) setCatalogLoadStatuses((current) => ({ ...current, [code]: "ready" }));
        },
        () => {
          if (!cancelled) setCatalogLoadStatuses((current) => ({ ...current, [code]: "error" }));
        }
      );
    }
    return () => {
      cancelled = true;
    };
  }, [chapterItems, catalogLoadAttempt]);
  const chapterLanguageOptions = useMemo(() => {
    const options = new Map<string, { journey: LanguageJourney; count: number }>();

    for (const item of chapterItems) {
      const languageCode = getPodcastChapterLanguageCode(item);
      if (selectedLanguageCode && languageCode !== selectedLanguageCode) continue;

      const journey = getLanguageJourneyByCode(languageCode);
      if (!journey) continue;

      const current = options.get(journey.languageCode);
      options.set(journey.languageCode, {
        journey,
        count: (current?.count || 0) + 1,
      });
    }

    return Array.from(options.values()).sort((a, b) =>
      a.journey.languageName.localeCompare(b.journey.languageName)
    );
  }, [chapterItems, selectedLanguageCode, journeyCatalogRevision]);
  const activeChapterLanguage =
    selectedLanguageCode ||
    selectedChapterLanguageCode ||
    chapterLanguageOptions[0]?.journey.languageCode ||
    null;
  const activeJourney =
    chapterLanguageOptions.find((option) => option.journey.languageCode === activeChapterLanguage)
      ?.journey || null;
  const activeChapterItems = useMemo(
    () =>
      activeJourney
        ? chapterItems.filter(
            (item) => getPodcastChapterLanguageCode(item) === activeJourney.languageCode
          )
        : [],
    [activeJourney, chapterItems]
  );
  const otherItems = feedItems.filter((item) => item.source !== "chapter" || !item.chapterId);
  // Feed metadata is sufficient to open an episode even when its course chunk fails.
  const unresolvedChapterItems = chapterItems.filter((item) => {
    const code = getPodcastChapterLanguageCode(item);
    return (
      (!selectedLanguageCode || code === selectedLanguageCode) && !getLanguageJourneyByCode(code)
    );
  });
  const hasCatalogError = unresolvedChapterItems.some((item) => {
    const code = getPodcastChapterLanguageCode(item);
    return code && catalogLoadStatuses[code] === "error";
  });
  const isCatalogLoading = unresolvedChapterItems.some((item) => {
    const code = getPodcastChapterLanguageCode(item);
    return code && (!catalogLoadStatuses[code] || catalogLoadStatuses[code] === "loading");
  });

  useEffect(() => {
    if (selectedLanguageCode) {
      setSelectedChapterLanguageCode(selectedLanguageCode);
      return;
    }

    if (
      !selectedChapterLanguageCode ||
      !chapterLanguageOptions.some(
        (option) => option.journey.languageCode === selectedChapterLanguageCode
      )
    ) {
      setSelectedChapterLanguageCode(chapterLanguageOptions[0]?.journey.languageCode || null);
    }
  }, [chapterLanguageOptions, selectedChapterLanguageCode, selectedLanguageCode]);

  if (isFeedLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center rounded-lg border border-white/10 bg-[#121318]">
        <Loader2 className="animate-spin text-orange-200" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-black uppercase text-orange-300/70">Podcast library</p>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-black sm:text-2xl">Listening Room</h2>
            <BetaBadge />
          </div>
          <p className="mt-1 text-sm text-white/55">
            Public listening tracks for structured chapters and standalone practice. Chapter
            coverage is still rolling out.
          </p>
        </div>
      </div>

      {isCatalogLoading && (
        <p role="status" className="flex items-center gap-2 text-sm text-white/55">
          <Loader2 size={16} className="animate-spin" />
          Loading chapter details…
        </p>
      )}
      {hasCatalogError && (
        <div role="alert" className="rounded-lg border border-white/10 bg-[#121318] p-4">
          <p className="text-sm text-white/70">
            Chapter details could not load. You can still open available episodes.
          </p>
          <button
            type="button"
            onClick={() => setCatalogLoadAttempt((attempt) => attempt + 1)}
            className={cn(cinematicModalSecondaryButtonClass, "mt-3")}
          >
            Retry chapter details
          </button>
        </div>
      )}

      {chapterLanguageOptions.length > 1 && !selectedLanguageCode && (
        <div className="rounded-lg border border-white/10 bg-[#121318] p-3">
          <div className="mb-2 text-[10px] font-black uppercase text-white/40">
            Chapter language
          </div>
          <div className="flex flex-wrap gap-2">
            {chapterLanguageOptions.map((option) => {
              const isActive = option.journey.languageCode === activeJourney?.languageCode;

              return (
                <button
                  key={option.journey.languageCode}
                  type="button"
                  onClick={() => setSelectedChapterLanguageCode(option.journey.languageCode)}
                  className={cn(
                    "inline-flex min-h-11 items-center gap-2 rounded-lg border px-4 py-2 text-sm font-bold transition-colors",
                    isActive
                      ? "border-orange-300/55 bg-orange-500/10 text-white"
                      : "border-white/10 bg-white/[0.025] text-white/55 hover:border-orange-300/30 hover:bg-white/[0.055] hover:text-white"
                  )}
                >
                  <span>{option.journey.languageName}</span>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[11px]",
                      isActive ? "bg-black/10 text-black" : "bg-white/10 text-white/60"
                    )}
                  >
                    {option.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {activeJourney && activeChapterItems.length > 0 && (
        <ChapterPodcastPath
          journey={activeJourney}
          chapterItems={activeChapterItems}
          onOpen={onOpen}
        />
      )}

      {unresolvedChapterItems.length > 0 && (
        <section aria-label="Chapter episodes" className="grid gap-3 md:grid-cols-2">
          {unresolvedChapterItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onOpen(item)}
              className="rounded-lg border border-white/10 bg-[#121318] p-4 text-left transition-colors hover:border-orange-300/30 hover:bg-white/[0.04]"
            >
              <h3 className="font-semibold text-white">{item.title}</h3>
              <p className="mt-2 text-sm text-white/55">
                {item.targetLanguage} · {item.cefrLevel}
              </p>
            </button>
          ))}
        </section>
      )}

      {otherItems.length === 0 &&
      activeChapterItems.length === 0 &&
      unresolvedChapterItems.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-[#121318] p-8 text-sm text-white/55">
          No podcasts found for this filter yet.
        </div>
      ) : (
        otherItems.length > 0 && (
          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-white/80">Standalone listening tracks</h3>
              <p className="mt-1 text-xs text-white/45">
                Generated and manual podcasts that are not linked to a structured chapter.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {otherItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onOpen(item)}
                  className="rounded-lg border border-white/10 bg-[#121318] p-4 text-left transition-colors hover:border-orange-300/30 hover:bg-white/[0.04]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-white">{item.title}</h3>
                      <p className="mt-2 line-clamp-2 text-sm text-white/55">{item.topic}</p>
                    </div>
                    {item.visibility === "public" ? <Globe2 size={17} /> : <Lock size={17} />}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs text-white/55">
                    <span className="rounded-full bg-white/10 px-2 py-1">
                      {item.targetLanguage}
                    </span>
                    <span className="rounded-full bg-white/10 px-2 py-1">{item.cefrLevel}</span>
                    <span className="rounded-full bg-white/10 px-2 py-1">{item.status}</span>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )
      )}

      {hasMore ? (
        <div className="flex justify-center pt-1">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isFeedLoadingMore}
            className={cn(cinematicModalSecondaryButtonClass, "min-w-44")}
          >
            {isFeedLoadingMore ? <Loader2 size={16} className="animate-spin" /> : null}
            {isFeedLoadingMore ? "Loading podcasts" : "Load more podcasts"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ChapterPodcastPath({
  journey,
  chapterItems,
  onOpen,
}: {
  journey: LanguageJourney;
  chapterItems: PodcastProject[];
  onOpen: (project: PodcastProject) => void;
}) {
  const chapterGroups = useMemo(() => getLanguageJourneyChapterGroups(journey), [journey]);
  const [selectedLevel, setSelectedLevel] = useState(chapterGroups[0]?.id || "A1");
  const selectedLevelGroup =
    chapterGroups.find((group) => group.id === selectedLevel) || chapterGroups[0];
  const episodesByChapter = useMemo(
    () => new Map(chapterItems.map((item) => [item.chapterId, item])),
    [chapterItems]
  );

  useEffect(() => {
    if (!chapterGroups.some((group) => group.id === selectedLevel)) {
      setSelectedLevel(chapterGroups[0]?.id || "A1");
    }
  }, [chapterGroups, selectedLevel]);

  return (
    <section className="overflow-hidden rounded-lg border border-white/10 bg-[#121318]">
      <div className="border-b border-white/10 bg-[#121318] p-4 sm:p-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-black uppercase text-orange-300/70">
              <Headphones size={14} />
              Chapter Podcasts
              <BetaBadge />
            </div>
            <h3 className="mt-1 text-xl font-bold tracking-tight text-white sm:text-2xl">
              {journey.languageName} listening path
            </h3>
            {journey.sourceLabel && (
              <div className="mt-2 flex items-center gap-2">
                {journey.sourceLogoSrc && (
                  <img
                    src={journey.sourceLogoSrc}
                    alt={journey.sourceLogoAlt || journey.sourceLabel}
                    className="h-12 w-auto max-w-[13rem] shrink-0 object-contain"
                  />
                )}
                <p className="text-xs font-bold text-white/70">{journey.sourceLabel}</p>
              </div>
            )}
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/45">
              Listen through the same chapter path as Language Journey. Chapter episodes are public
              by default, but this beta library is still being filled in.
            </p>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.025] px-4 py-3 text-sm font-bold text-white/55">
            {chapterItems.length}/{journey.chapters.length} episodes
          </div>
        </div>
      </div>

      <div className="grid min-h-0 grid-cols-1 lg:grid-cols-[14rem_1fr]">
        <div className="border-b border-white/10 bg-[#121318] p-2 sm:p-3 lg:border-b-0 lg:border-r lg:p-4">
          <div className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible">
            {chapterGroups.map((group) => {
              const availableInLevel = group.chapters.filter((chapter) =>
                episodesByChapter.has(chapter.id)
              ).length;
              const isActive = group.id === selectedLevel;

              return (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => setSelectedLevel(group.id)}
                  className={cn(
                    "min-w-[3.5rem] rounded-lg border px-3 py-2 text-center transition-colors sm:min-w-[8rem] sm:px-4 sm:py-3 sm:text-left lg:min-w-0",
                    isActive
                      ? "border-orange-300/55 bg-orange-500/10 text-white"
                      : "border-white/10 bg-white/[0.025] text-white/55 hover:border-orange-300/30 hover:bg-white/[0.055] hover:text-white"
                  )}
                >
                  <span className="block text-sm font-black">{group.label}</span>
                  <span className="mt-1 hidden text-[11px] font-semibold opacity-70 sm:block">
                    {availableInLevel}/{group.chapters.length} live
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="min-h-0 bg-[#0f0f14] p-3 sm:p-6">
          {selectedLevelGroup && (
            <>
              <div className="mb-3 max-w-3xl sm:mb-6">
                <p className="text-sm font-bold text-white">{selectedLevelGroup.title}</p>
                <p className="mt-1 hidden text-sm leading-relaxed text-white/45 sm:block">
                  {selectedLevelGroup.canDo}
                </p>
              </div>

              <div className="relative pb-2">
                <div className="absolute bottom-0 top-0 hidden w-px bg-white/10 md:left-1/2 md:block" />
                <div className="space-y-3 sm:space-y-5">
                  {selectedLevelGroup.chapters.map((chapter, index) => {
                    const episode = episodesByChapter.get(chapter.id);
                    const isLeft = index % 2 === 0;

                    return (
                      <div
                        key={chapter.id}
                        className={cn(
                          "relative flex md:pl-0",
                          isLeft ? "md:justify-start md:pr-[53%]" : "md:justify-end md:pl-[53%]"
                        )}
                      >
                        <span
                          className={cn(
                            "absolute left-5 top-5 z-10 hidden h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full border text-sm font-black md:left-1/2 md:flex",
                            episode
                              ? "border-orange-300/70 bg-orange-500 text-white"
                              : "border-white/10 bg-[#15161b] text-white/25"
                          )}
                        >
                          {episode ? <Headphones size={17} /> : chapter.cefrLevelChapterNumber}
                        </span>

                        <button
                          type="button"
                          disabled={!episode}
                          onClick={() => episode && onOpen(episode)}
                          className={cn(
                            "w-full rounded-lg border p-3 text-left transition-colors sm:p-4",
                            episode
                              ? "cursor-pointer border-white/10 bg-white/[0.035] hover:border-orange-200/50 hover:bg-white/[0.07] focus:border-orange-200/60 focus:outline-none focus:ring-2 focus:ring-orange-300/20"
                              : "cursor-not-allowed border-white/10 bg-white/[0.02] opacity-55"
                          )}
                        >
                          <span className="flex flex-wrap items-center gap-2">
                            <span
                              className={cn(
                                "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-black md:hidden",
                                episode
                                  ? "border-orange-300/70 bg-orange-500 text-white"
                                  : "border-white/10 bg-[#15161b] text-white/25"
                              )}
                            >
                              {episode ? <Headphones size={14} /> : chapter.cefrLevelChapterNumber}
                            </span>
                            <span className="text-sm font-bold text-white sm:text-base">
                              {chapter.title}
                            </span>
                            <span className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] font-black uppercase tracking-normal text-white/40">
                              {chapter.cefrLevel}.{chapter.cefrLevelChapterNumber}
                            </span>
                            {episode ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-normal text-emerald-300">
                                <CheckCircle2 size={11} />
                                Public
                              </span>
                            ) : (
                              <span className="rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10px] font-black uppercase tracking-normal text-white/25">
                                Coming soon
                              </span>
                            )}
                          </span>
                          <span className="mt-2 block line-clamp-2 text-xs leading-relaxed text-white/45 sm:text-sm">
                            {episode?.topic ||
                              getLanguageJourneyChapterDisplayDescription(journey, chapter)}
                          </span>
                          <span className="mt-3 hidden text-xs font-semibold text-white/30 sm:block">
                            {getLanguageJourneyChapterDisplayFocus(journey, chapter)}
                          </span>
                          {episode ? (
                            <span className="mt-4 flex flex-wrap gap-2">
                              <span className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-3 py-2 text-xs font-bold text-white transition-colors">
                                Listen
                              </span>
                            </span>
                          ) : null}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function EditorView({
  project,
  projectCanSave,
  allAudioReady,
  audioReadyCount,
  isPlaying,
  playingIndex,
  editingSegmentId,
  draftText,
  newSegmentSpeaker,
  newSegmentText,
  onSetProject,
  onSave,
  onGenerateAudio,
  onPlay,
  onStop,
  onDownload,
  onPlaySegment,
  onEditStart,
  onEditCancel,
  onDraftTextChange,
  onEditSave,
  onRegenerateSegmentAudio,
  onNewSegmentSpeakerChange,
  onNewSegmentTextChange,
  onAddSegment,
  onBack,
}: {
  project: PodcastProject;
  projectCanSave: boolean;
  allAudioReady: boolean;
  audioReadyCount: number;
  isPlaying: boolean;
  playingIndex: number | null;
  editingSegmentId: string | null;
  draftText: string;
  newSegmentSpeaker: PodcastSpeakerRole;
  newSegmentText: string;
  onSetProject: (project: PodcastProject) => void;
  onSave: () => void;
  onGenerateAudio: () => void;
  onPlay: () => void;
  onStop: () => void;
  onDownload: () => void;
  onPlaySegment: (index: number) => void;
  onEditStart: (segment: PodcastTranscriptSegment) => void;
  onEditCancel: () => void;
  onDraftTextChange: (value: string) => void;
  onEditSave: (segment: PodcastTranscriptSegment) => void;
  onRegenerateSegmentAudio: (segmentId: string) => void;
  onNewSegmentSpeakerChange: (speaker: PodcastSpeakerRole) => void;
  onNewSegmentTextChange: (text: string) => void;
  onAddSegment: () => void;
  onBack: () => void;
}) {
  const segmentRefs = useRef<Array<HTMLElement | null>>([]);

  useEffect(() => {
    if (!isPlaying || playingIndex === null) return;
    segmentRefs.current[playingIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [isPlaying, playingIndex]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          className={cn(cinematicModalSecondaryButtonClass, "h-10 px-3 text-xs")}
        >
          <ChevronLeft size={16} />
          Listening Room
        </button>
        <div className="text-[10px] font-black uppercase text-white/40">
          {project.segments.length} segments / {audioReadyCount} audio ready
        </div>
      </div>

      <section className="rounded-lg border border-white/10 bg-[#121318] p-4 sm:p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase text-orange-300/70">Current track</p>
            <input
              value={project.title}
              onChange={(event) => onSetProject({ ...project, title: event.target.value })}
              aria-label="Podcast title"
              className="mt-1 w-full border-b border-transparent bg-transparent pb-1 text-2xl font-black tracking-tight text-white outline-none transition-colors hover:border-white/10 focus:border-orange-300/55 sm:text-3xl"
            />
            <p className="mt-2 text-sm text-white/55">{project.topic}</p>
            <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-black uppercase text-white/45">
              <span className="rounded-full border border-white/10 bg-white/[0.025] px-2.5 py-1">
                {project.targetLanguage}
              </span>
              <span className="rounded-full border border-white/10 bg-white/[0.025] px-2.5 py-1">
                {project.cefrLevel}
              </span>
              <span className="rounded-full border border-white/10 bg-white/[0.025] px-2.5 py-1">
                {project.source}
              </span>
              <BetaBadge />
              <span className="rounded-full border border-white/10 bg-white/[0.025] px-2.5 py-1">
                {audioReadyCount}/{project.segments.length} audio
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {podcastHasDiscussableScript(project) ? (
              <Link
                href={getPodcastDiscussionHref(project)}
                className={cn(cinematicModalSecondaryButtonClass, "h-10 px-3 text-xs")}
              >
                <MessageCircle size={16} />
                Discuss
              </Link>
            ) : null}
            <button
              onClick={onSave}
              disabled={!projectCanSave}
              className={cn(cinematicModalSecondaryButtonClass, "h-10 px-3 text-xs")}
            >
              <Save size={16} />
              Save
            </button>
            <button
              onClick={onGenerateAudio}
              disabled={allAudioReady || project.segments.length === 0}
              className={cn(
                audioReadyCount === 0
                  ? cinematicModalPrimaryButtonClass
                  : cinematicModalSecondaryButtonClass,
                "h-10 px-3 text-xs"
              )}
            >
              <Volume2 size={16} />
              Generate audio
            </button>
            {isPlaying ? (
              <button
                onClick={onStop}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-red-300/30 bg-red-500 px-3 text-xs font-bold text-white transition-colors hover:bg-red-400"
              >
                <StopCircle size={16} />
                Stop
              </button>
            ) : (
              <button
                onClick={onPlay}
                disabled={audioReadyCount === 0}
                className={cn(cinematicModalPrimaryButtonClass, "h-10 px-3 text-xs")}
              >
                <Play size={16} />
                Play
              </button>
            )}
            <button
              onClick={onDownload}
              disabled={audioReadyCount === 0}
              className={cn(cinematicModalSecondaryButtonClass, "h-10 px-3 text-xs")}
            >
              <Download size={16} />
              WAV
            </button>
          </div>
        </div>
        <div className="mt-5 flex h-12 items-end gap-1">
          {project.segments.map((segment, index) => (
            <button
              key={segment.id}
              onClick={() => onPlaySegment(index)}
              disabled={!segment.audioUrl}
              className={cn(
                "min-w-0 flex-1 rounded-t transition-all",
                playingIndex === index && isPlaying
                  ? "h-full bg-orange-400"
                  : segment.audioUrl
                    ? "h-7 bg-white/25 hover:h-10 hover:bg-orange-300/65"
                    : "h-2 bg-white/10"
              )}
              title={getPodcastSpeakerLabel(segment)}
            />
          ))}
        </div>
      </section>

      <div className="grid gap-4">
        <section className="overflow-hidden rounded-lg border border-white/10 bg-[#121318]">
          <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
            <div>
              <p className="text-[10px] font-black uppercase text-orange-300/70">Transcript</p>
              <h2 className="mt-0.5 text-sm font-black text-white">Script and segment audio</h2>
            </div>
            <span className="text-[10px] font-black uppercase text-white/35">
              {project.segments.length} lines
            </span>
          </div>
          {project.segments.map((segment, index) => {
            const isActive = playingIndex === index && isPlaying;
            const isEditing = editingSegmentId === segment.id;
            return (
              <article
                key={segment.id}
                ref={(element) => {
                  segmentRefs.current[index] = element;
                }}
                className={cn(
                  "scroll-mt-24 border-b border-white/10 p-4 transition-colors last:border-b-0",
                  isActive
                    ? "bg-orange-500/[0.08] shadow-[inset_3px_0_0_#f97316]"
                    : "bg-transparent hover:bg-white/[0.015]"
                )}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-[10px] font-black uppercase text-white/45">
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 bg-white/[0.025] text-orange-300">
                      <Mic2 size={13} />
                    </span>
                    {getPodcastSpeakerLabel(segment)}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] font-semibold text-white/40">
                    {segment.audioUrl ? (
                      <span className="inline-flex items-center gap-1 text-emerald-200">
                        <Check size={14} />
                        Audio ready
                      </span>
                    ) : (
                      "No audio"
                    )}
                    {segment.isEdited && <span>Edited</span>}
                  </div>
                </div>

                {isEditing ? (
                  <div className="mt-3 space-y-3">
                    <textarea
                      value={draftText}
                      onChange={(event) => onDraftTextChange(event.target.value)}
                      className={cn(cinematicModalTextareaClass, "min-h-[120px]")}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={onEditCancel}
                        className={cn(cinematicModalSecondaryButtonClass, "h-9 px-3 text-xs")}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => onEditSave(segment)}
                        className={cn(cinematicModalPrimaryButtonClass, "h-9 px-3 text-xs")}
                      >
                        Save changes
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => onEditStart(segment)}
                      className="mt-3 block w-full text-left text-[15px] leading-7 text-white/80 transition-colors hover:text-white"
                    >
                      {segment.text}
                    </button>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        onClick={() => onEditStart(segment)}
                        className={cn(cinematicModalSecondaryButtonClass, "h-9 px-3 text-xs")}
                      >
                        Edit text
                      </button>
                      <button
                        onClick={() => onRegenerateSegmentAudio(segment.id)}
                        className={cn(cinematicModalSecondaryButtonClass, "h-9 px-3 text-xs")}
                      >
                        {segment.audioUrl ? "Regenerate audio" : "Generate audio"}
                      </button>
                    </div>
                  </>
                )}
              </article>
            );
          })}

          <div className="border-t border-dashed border-white/15 bg-white/[0.018] p-4">
            <p className="mb-3 text-[10px] font-black uppercase text-orange-300/70">
              Add a segment
            </p>
            <div className="grid gap-3 md:grid-cols-[180px_1fr_auto] md:items-start">
              <select
                value={newSegmentSpeaker}
                onChange={(event) =>
                  onNewSegmentSpeakerChange(event.target.value as PodcastSpeakerRole)
                }
                className={cinematicModalSelectClass}
              >
                {Object.keys(PODCAST_VOICE_BY_SPEAKER).map((speaker) => (
                  <option key={speaker} value={speaker}>
                    {speaker}
                  </option>
                ))}
              </select>
              <textarea
                value={newSegmentText}
                onChange={(event) => onNewSegmentTextChange(event.target.value)}
                placeholder="Add a new line to the podcast..."
                className={cn(cinematicModalTextareaClass, "min-h-[78px]")}
              />
              <button
                onClick={onAddSegment}
                className={cn(cinematicModalSecondaryButtonClass, "h-11")}
              >
                <Plus size={16} />
                Add
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
